import { useAppRouter } from '@/common/route/useAppRouter';
import { validateDateAfter } from '@/common/validator/validatorHelpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { SelectOption } from '@/components/Select.vue';
import { useAppStore } from '@/modules/apps/store';
import { DisplayType } from '@/modules/promotion/item/model/mixins';
import { ROUTE_NAME_PROMOTION } from '@/modules/promotion/router';
import { promotionService } from '@/services/promotion';
import { rfmService } from '@/services/rfm';
import { useVuelidate } from '@vuelidate/core';
import { integer, minValue, required, requiredIf } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';

export function usePromotionItemAdd() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const { appId, appSelectOptions } = storeToRefs(useAppStore());

    const personaMap = computed(() => new Map(personaAllForAppId.value.map((item: any) => [item.id, item])));
    const { list: personaAllForAppId, fetchList: fetchPersonaAllForAppId } = useBaseListView({
        request: rfmService.persona.listForOtherApp,
        paramsBuilder: () => ({ forAppId: model.forAppId })
    });

    const { list: prototypeAllForAppId, fetchList: fetchPrototypeAllForAppId } = useBaseListView({
        request: promotionService.prototype.listAll,
        paramsBuilder: () => ({ forAppId: model.forAppId }),
        hooks: {
            loadInitialData: async () => await fetchPersonaAllForAppId(),
            canFetch: () => model.forAppId && personaAllForAppId.value.length > 0,
            transformResponse: (list: any[]) =>
                list.map((item: any) => {
                    item.personas.forEach((per: any) => {
                        per.rfmPersonaName = personaMap.value.get(per.rfmPersonaId)?.name;
                    });
                    return item;
                })
        }
    });

    const model = reactive({
        cloneId: route.params?.id ?? null,
        forAppId: appId.value,
        name: '',
        status: null,
        displayType: 0,
        fromTime: null,
        toTime: null,
        expectedPackage: 0,
        image: '',
        imageStatus: null,
        imageFile: null,
        prototypeId: null,
        get prototypeOptions(): SelectOption[] {
            return prototypeAllForAppId.value.map((item: any) => ({
                value: item.id,
                label: `[${item.id}] ${item.name}`
            }));
        },
        get personas() {
            return prototypeAllForAppId.value.find(item => item.id === model.prototypeId)?.personas ?? [];
        }
    });

    const validations = {
        name: { required },
        displayType: { required },
        expectedPackage: { integer, minValue: minValue(0) },
        prototypeId: { required, integer, minValue: minValue(0) },
        fromTime: { requiredIf: requiredIf(() => model.displayType !== DisplayType.AccessUnlimited.value) },
        toTime: {
            requiredIf: requiredIf(() => model.displayType !== DisplayType.AccessUnlimited.value),
            validDate: validateDateAfter('fromTime')
        }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = crudAction.executeAction({
        canExecute: () => model.cloneId != null,
        request: () => promotionService.item.get(model.cloneId),
        onSuccess: ({ data }) => {
            model.name = data.name;
            model.displayType = data.displayType;
            model.fromTime = data.fromTime ? new Date(data.fromTime) : null;
            model.toTime = data.toTime ? new Date(data.toTime) : null;
            model.expectedPackage = data.expectedPackage;
            model.image = data.image;
            model.prototypeId = data.usedPrototypeId;
        },
        onError: () => router.push({ name: ROUTE_NAME_PROMOTION.ITEM.INDEX })
    });

    const actions = {
        create: crudAction.excuteAndNotify({
            validator: v$,
            request: () =>
                promotionService.item.create(
                    model.name,
                    model.cloneId,
                    model.cloneId ? model.forAppId : null,
                    model.fromTime?.getTime(),
                    model.toTime?.getTime(),
                    model.displayType,
                    model.expectedPackage,
                    model.prototypeId,
                    model.imageFile
                ),
            onSuccess: () => router.push({ name: ROUTE_NAME_PROMOTION.ITEM.INDEX })
        })
    };

    // reload prototypes when appId changed
    watch(() => model.forAppId, fetchPrototypeAllForAppId);
    watch(
        () => model.displayType,
        displayType => {
            if (displayType === DisplayType.AccessUnlimited.value) {
                model.fromTime = null;
                model.toTime = null;
            }
        }
    );

    onMounted(async () => {
        await fetchPrototypeAllForAppId();
        await fetch();
    });

    return { model, v$, fetch, appSelectOptions, actions };
}
